<template>
  <div class="dual-listbox">
    <div class="list-container">
      <div class="list-header">
        <div class="label"><i class="far fa-square"></i> {{ getLang('available') }}</div>
        <div class="searcher-input">
          <input v-model="notSelectedSearcher" @input="doNotSelectedSearch()" />
          <transition name="fade" mode="out-in">
            <i v-if="notSelectedSearcher.trim() === ''" class="sherpa-icon icon-buscar"></i>
            <i v-else class="sherpa-icon icon-cerrar" @click="notSelectedSearcherReset()"></i>
          </transition>
          <transition name="fade" mode="out-in">
            <div class="searcher-count-results">{{ notSelectedCount }}/{{ notSelectedTotal }}</div>
          </transition>
        </div>
      </div>
      <button @click="moveAllToSelected" :disabled="notSelectedList.length === 0">
        {{ getLang('moveAll') }} <i class="fas fa-chevron-circle-right fa-sm"></i>
      </button>
      <simplebar data-simplebar-auto-hide="false">
        <draggable
          v-model="notSelectedList"
          item-key="id"
          v-bind="dragNotSelectedOptions"
          :class="['list', {'dragging-element' : draggingElement}]"
          @start="dragElementStart"
          @end="dragElementEnd"
        >
          <template #item="{ element: item, index }">
            <div
              :key="item.id"
              @click="toggleSelection(item, 'available', index, $event)"
              :class="['list-item', {'selected': selectedAvailable.includes(item)}]"
            >
              <span v-if="!hideIds" class="id">{{ item.id }}</span>
              <span class="name">{{ item.name }}</span>
            </div>
          </template>
        </draggable>
      </simplebar>
    </div>

    <div class="buttons">
      <button
        @click="moveSelectedToSelected"
        :disabled="!selectedAvailable.some(item => notSelectedList.includes(item))"
      >
        <i class="fas fa-chevron-circle-right fa-sm"></i>
      </button>

      <button
        @click="moveSelectedToAvailable"
        :disabled="!selectedChosen.some(item => selectedList.includes(item))"
      >
        <i class="fas fa-chevron-circle-left fa-sm"></i>
      </button>
    </div>

    <div class="list-container selected-list">
      <div class="list-header">
        <div class="label"><i class="far fa-check-square"></i> {{ getLang('selected') }}</div>
        <div class="searcher-input">
          <input v-model="selectedSearcher" @input="doSelectedSearch()" />
          <transition name="fade" mode="out-in">
            <i v-if="selectedSearcher.trim() === ''" class="sherpa-icon icon-buscar"></i>
            <i v-else class="sherpa-icon icon-cerrar" @click="selectedSearcherReset()"></i>
          </transition>
          <transition name="fade" mode="out-in">
            <div class="searcher-count-results">{{ selectedCount }}/{{ selectedTotal }}</div>
          </transition>
        </div>
      </div>
      <button @click="moveAllToAvailable" :disabled="selectedList.length === 0">
        <i class="fas fa-chevron-circle-left fa-sm"></i> {{ getLang('moveAll') }}
      </button>
      <simplebar data-simplebar-auto-hide="false">
        <draggable
          v-model="selectedList"
          item-key="id"
          v-bind="dragSelectedOptions"
          :class="['list', {'dragging-element' : draggingElement}]"
          @start="dragElementStart"
          @end="dragElementEnd"
        >
          <template #item="{ element: item, index }">
            <div
              :key="item.id"
              @click="toggleSelection(item, 'chosen', index, $event)"
              :class="['list-item', {'selected': selectedChosen.includes(item)}]"
            >
              <span v-if="!hideIds" class="id">{{ item.id }}</span>
              <span class="name">{{ item.name }}</span>
            </div>
          </template>
        </draggable>
      </simplebar>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import simplebar from 'simplebar-vue'

export default {
  name: 'dual-list',
  components: {
    draggable,
    simplebar
  },
  props: {
    domain: {
      type: Object,
      required: true
    },
    getLang: Function,
    selected: Boolean,
    hideIds: Boolean
  },
  data () {
    return {
      uid: Math.random().toString(36).slice(2),
      selectedAvailable: [], // Elementos seleccionados en la lista "Disponibles"
      selectedChosen: [], // Elementos seleccionados en la lista "Seleccionados"
      lastSelectedIndex: null,
      draggingElement: false,
      selectedCount: 0,
      selectedTotal: 0,
      selectedSearcher: '',
      selectedList: [],
      notSelectedCount: 0,
      notSelectedTotal: 0,
      notSelectedSearcher: '',
      notSelectedList: []
    }
  },
  beforeMount () {
    this.refreshLists()
  },
  methods: {
    refreshLists () {
      this.$nextTick(() => {
        this.doSelectedSearch()
        this.doNotSelectedSearch()
      })
    },
    toggleSelection (item, listType, index, event) {
      let list = listType === 'available' ? this.selectedAvailable : this.selectedChosen
      let items = listType === 'available' ? this.domain.notSelected : this.domain.selected

      // Manejo de selección múltiple (SHIFT)
      if (event.shiftKey && this.lastSelectedIndex !== null) {
        const start = Math.min(this.lastSelectedIndex, index)
        const end = Math.max(this.lastSelectedIndex, index)
        for (let i = start; i <= end; i++) {
          if (!list.includes(items[i])) {
            list.push(items[i])
          }
        }
      } else {
        this.toggleItem(item, list)
        this.lastSelectedIndex = index
      }
    },
    toggleItem (item, list) {
      const index = list.indexOf(item)
      if (index > -1) {
        list.splice(index, 1)
      } else {
        list.push(item)
      }
    },
    moveSelectedToSelected () {
      // Filtrar solo los elementos que realmente están en notSelected
      const validItems = this.selectedAvailable.filter(item =>
        this.notSelectedList.includes(item)
      )

      if (validItems.length === 0) return // Evita ejecutar cambios si no hay elementos válidos

      // Mover los elementos válidos a la lista "Seleccionados"
      this.domain.selected.push(...validItems)

      // Eliminar los elementos válidos de la lista "No seleccionados"
      this.domain.notSelected = this.domain.notSelected.filter(item =>
        !validItems.includes(item)
      )

      // Limpiar la selección
      this.selectedAvailable = []

      // Refrescar las listas
      this.refreshLists()
    },
    moveSelectedToAvailable () {
      // Filtrar solo los elementos que realmente están en selectedList
      const validItems = this.selectedChosen.filter(item =>
        this.selectedList.includes(item)
      )

      if (validItems.length === 0) return // Evita ejecutar cambios si no hay elementos válidos

      // Mover los elementos válidos a la lista "Disponibles"
      this.domain.notSelected.push(...validItems)

      // Eliminar los elementos válidos de la lista "Seleccionados"
      this.domain.selected = this.domain.selected.filter(item =>
        !validItems.includes(item)
      )

      // Limpiar la selección
      this.selectedChosen = []

      // Resetear selectedByDefault
      this.domain.notSelected.forEach((item, index) => {
        item.selectedByDefault = false
      })

      // Refrescar las listas
      this.refreshLists()
    },
    moveAllToSelected () {
      // Filtrar solo los elementos visibles de la lista de no seleccionados
      const filteredNotSelected = this.domain.notSelected.filter(item =>
        this.normalizeText(item.name).indexOf(this.normalizeText(this.notSelectedSearcher.trim())) > -1 ||
        this.normalizeText(item.id).indexOf(this.normalizeText(this.notSelectedSearcher.trim())) > -1
      )

      // Mover solo los elementos filtrados a la lista de seleccionados
      this.domain.selected.push(...filteredNotSelected)

      // Eliminar solo los elementos filtrados de la lista de no seleccionados
      this.domain.notSelected = this.domain.notSelected.filter(item =>
        !filteredNotSelected.includes(item)
      )

      // Limpiar la selección
      this.selectedAvailable = []

      this.refreshLists()
    },
    moveAllToAvailable () {
      // Filtrar solo los elementos visibles de la lista de seleccionados
      const filteredSelected = this.domain.selected.filter(item =>
        this.normalizeText(item.name).indexOf(this.normalizeText(this.selectedSearcher.trim())) > -1 ||
        this.normalizeText(item.id).indexOf(this.normalizeText(this.selectedSearcher.trim())) > -1
      )

      // Mover solo los elementos filtrados a la lista de no seleccionados
      this.domain.notSelected.push(...filteredSelected)

      // Eliminar solo los elementos filtrados de la lista de seleccionados
      this.domain.selected = this.domain.selected.filter(item =>
        !filteredSelected.includes(item)
      )

      // Resetear selectedByDefault
      this.domain.notSelected.forEach((item, index) => {
        item.selectedByDefault = false
      })

      // Limpiar la selección
      this.selectedChosen = []

      this.refreshLists()
    },
    dragElementStart () {
      this.draggingElement = true
    },
    dragElementEnd () {
      this.draggingElement = false

      // Clonar las listas originales antes de la actualización
      const prevSelected = [...this.domain.selected]
      const prevNotSelected = [...this.domain.notSelected]

      // Comparar si se ha movido un elemento entre listas
      const selectedChanged = this.selectedList.some(item => !prevSelected.includes(item)) ||
                              prevSelected.some(item => !this.selectedList.includes(item))
      const notSelectedChanged = this.notSelectedList.some(item => !prevNotSelected.includes(item)) ||
                                prevNotSelected.some(item => !this.notSelectedList.includes(item))

      // Si se movió un elemento de "notSelected" a "selected"
      if (selectedChanged && this.selectedList.length > prevSelected.length) {
        // Añadir elementos de selectedAvailable que están en notSelectedList a selectedList
        const availableToAdd = this.selectedAvailable.filter(item => this.notSelectedList.includes(item))
        this.selectedList = [...this.selectedList, ...availableToAdd]
        this.domain.selected = [...this.selectedList]
        this.domain.notSelected = prevNotSelected.filter(item => !this.selectedList.includes(item))

        // Limpiar la selección
        this.selectedAvailable = []

      // Si se movió un elemento de "selected" a "notSelected"
      } else if (notSelectedChanged && this.notSelectedList.length > prevNotSelected.length) {
        // Añadir elementos de selectedChosen que están en selectedList a notSelectedList
        const chosenToAdd = this.selectedChosen.filter(item => this.selectedList.includes(item))
        this.notSelectedList = [...this.notSelectedList, ...chosenToAdd]
        this.domain.notSelected = [...this.notSelectedList]
        this.domain.selected = prevSelected.filter(item => !this.notSelectedList.includes(item))

        // Limpiar la selección
        this.selectedChosen = []

        // Resetear selectedByDefault
        this.domain.notSelected.forEach((item, index) => {
          item.selectedByDefault = false
        })

      // Si solo se reordenaron elementos dentro de la misma lista, no hacer cambios
      } else {
        this.domain.selected = [...this.selectedList]
        this.domain.notSelected = [...this.notSelectedList]
      }

      // Recalcular las listas después de mover los elementos
      this.refreshLists()
    },
    doSearch (list, originList, searcher) {
      let count = 0
      let total = 0
      for (var i = 0; i < originList.length; i++) {
        var response = originList[i]

        total += 1
        if (!searcher || searcher.trim() === '') {
          count += 1
          list.push(response)
        } else {
          if (
            this.normalizeText(response.name).indexOf(this.normalizeText(searcher.trim())) > -1 ||
            this.normalizeText(response.id).indexOf(this.normalizeText(searcher.trim())) > -1
          ) {
            count += 1
            list.push(response)
          }
        }
      }
      return {count, total}
    },
    doSelectedSearch () {
      this.selectedList = []
      let counter = this.doSearch(this.selectedList, this.domain.selected, this.selectedSearcher)
      this.selectedCount = counter.count
      this.selectedTotal = counter.total
    },
    doNotSelectedSearch () {
      this.notSelectedList = []
      let counter = this.doSearch(this.notSelectedList, this.domain.notSelected, this.notSelectedSearcher)
      this.notSelectedCount = counter.count
      this.notSelectedTotal = counter.total
    },
    selectedSearcherReset () {
      this.selectedSearcher = ''
      this.doSelectedSearch()
    },
    notSelectedSearcherReset () {
      this.notSelectedSearcher = ''
      this.doNotSelectedSearch()
    },
    normalizeText (text) {
      return text
        .toLowerCase() // Convertir a minúsculas
        .normalize('NFD') // Descomponer caracteres acentuados
        .replace(/[\u0300-\u036f]/g, '') // Eliminar diacríticos
    },
    refreshSelected () {
      this.domain.selected = [...this.selectedList]
      this.refreshLists()
    },
    refreshNotSelected () {
      this.domain.notSelected = [...this.notSelectedList]
      this.refreshLists()
    },
    selectByDefaultChange (toggle) {
      this.refreshSelected()
    },
    notSelectedTextChange () {
      this.refreshNotSelected()
    },
    selectedTextChange () {
      this.refreshSelected()
    },
    countSelectByDefault () {
      const selected = this.domain.selected || []
      return selected.reduce((acc, item) => acc + (item.selectedByDefault ? 1 : 0), 0)
    }
  },
  computed: {
    dragNotSelectedOptions () {
      return {
        animation: 200,
        group: {
          name: `not-selected-${this.uid}`,
          put: [`selected-${this.uid}`, `not-selected-${this.uid}`],
          pull: true
        },
        disabled: false,
        ghostClass: 'ghost',
        sort: false
      }
    },
    dragSelectedOptions () {
      return {
        animation: 200,
        group: {
          name: `selected-${this.uid}`,
          put: [`selected-${this.uid}`, `not-selected-${this.uid}`],
          pull: true
        },
        disabled: false,
        ghostClass: 'ghost',
        sort: true
      }
    }
  },
  watch: {
  }
}
</script>

<style lang="scss">
$primary-color: #3498db;
$secondary-color: #ccc;
$border-color: #ccc;
$highlight-color: #50dfff; // Color para destacar el elemento seleccionado
$padding-size: 0;
$gap-size: 5px;

.dual-listbox {
  display: flex;
  align-items: flex-start;
  margin-top: $gap-size;
  padding-right: 20px;

  .list-container {
    width: 50%;
    padding: $padding-size;
    text-align: center;
    max-height: 250px;
    overflow: hidden;
    position: relative !important;

    .list-header {
      display: flex;
      flex-direction: row;
      align-items: center;

      .label {
        font-size: 12px;
        color: #555;
        width: 31%;
      }

      .searcher-input {
        padding: 0 0 0 10px;
        width: 100%;

        i {
          top: 7px;
          right: 6px;
          font-size: 13px;
        }

        input {
          padding: 2px 50px 2px 7px;
          font-size: 13px;
        }

        .searcher-count-results {
          top: 7px;
          right: 29px;
        }
      }
    }

    [data-simplebar] {
      max-height: 155px;
    }

    .list {
      display: flex;
      flex-direction: column;
      gap: 0;
      min-height: 33px;
      margin: 0;
      border: 1.5px dashed transparent;

      &.dragging-element {
        border: 1.5px dashed #d42f12cb;
      }

      .list-item {
        padding: 3px 5px 2px 2px;
        cursor: pointer;
        background: white;
        border: 1px solid $border-color;
        margin: 0;
        transition: background 0.3s, transform 0.3s;
        cursor: move;
        font-size: 14px;
        font-family:'Lucida Sans';
        text-align: left;
        position: relative;

        &.selected,
        &:hover {
          background: #3498db;
          color: white;

          .language-count {
            color: white;

            i {
              color: white;
            }
          }
        }

        .id {
          display: inline-block;
          margin-left: 2px;
          color: #444;
          font-size: 10px;
          vertical-align: top;
          min-width: 125px;
          background-color: #f5f5f5;
          text-align: center;
          border-radius: 2px;
          padding: 5px 5px 4px;
        }

        .name {
          display: inline-block;
          margin-left: 2px;
          color: #333;
          font-size: 12px;
          vertical-align: top;
          min-width: 125px;
          background-color: transparent;
          text-align: left;
          border-radius: 2px;
          padding: 3px 5px 2px;
        }

        .empty-name {
          font-size: 12px;
        }

        .vue-js-switch {
          position: absolute;
          right: 5px;
          top: 4px;
        }
      }
    }

    &.selected-list {
      .list-item {
        padding: 3px 125px 2px 2px;
      }
    }
  }

  .buttons {
    display: flex;
    flex-direction: column;
    padding: 74.5px 8px 0;

    button {
      padding: 7px 5px 3px;
      margin-bottom: 6px;
    }
  }

  button {
    margin: 0;
    padding: 6px 5px 4px;
    cursor: pointer;
    border: none;
    background-color: #e4fffe;
    border: 1px solid #ddd;
    border-radius: 0;
    transition: all 0.2s ease;
    font-family: 'Lucida Sans';
    font-size: 11px;

    i {
      font-size: 14px !important;
      color: #d42e12 !important;
      vertical-align: text-bottom !important;
    }

    &:disabled {
      background: #f5f5f5;
      cursor: not-allowed;

      i {
        color: #999 !important;
      }
    }

    &:not(:disabled):hover {
      border: 1px solid #999;
    }
  }

  .fade {
    &-enter-active, &-leave-active {
      transition: opacity 0.3s ease, transform 0.3s ease;
    }
    &-enter-from, &-leave-to {
      opacity: 0;
      transform: translateY(-10px);
    }
    &-enter-to, &-leave-from {
      opacity: 1;
      transform: translateY(0);
    }
  }
}
</style>
